import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	CircularProgress,
	Avatar,
	Radio,
	Chip,
	TextField,
	InputAdornment,
	Grid,
	IconButton,
	Tooltip,
} from '@material-ui/core';
import {LocalShipping, Search, Close} from '@material-ui/icons';
import SportsMotorsportsIcon from '@material-ui/icons/SportsMotorsports';
import {useOrders} from '../context/OrdersContext';
import API from '../../../../network/dashboardAPI';
import useAlert from '../../../../services/useAlert';

const useStyles = makeStyles((theme) => ({
	dialog: {
		'& .MuiDialog-paper': {
			minWidth: 700,
			maxWidth: 900,
		},
	},
	dialogTitle: {
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	orderInfo: {
		padding: theme.spacing(2),
		backgroundColor: '#f5f7fa',
		borderRadius: 8,
		marginBottom: theme.spacing(2),
	},
	// The two slots sit side by side so both assignments are visible at once, and
	// clicking one aims the driver grid below at that slot.
	slotCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 6,
		padding: theme.spacing(1.5),
		cursor: 'pointer',
		height: '100%',
		transition: 'all 0.15s',
		'&:hover': {
			borderColor: theme.palette.primary.light,
			backgroundColor: '#fafafa',
		},
	},
	activeSlotCard: {
		borderColor: theme.palette.primary.main,
		borderWidth: 2,
		padding: `calc(${theme.spacing(1.5)}px - 1px)`,
		backgroundColor: '#f3f9ff',
	},
	disabledSlotCard: {
		opacity: 0.55,
		cursor: 'not-allowed',
		'&:hover': {
			borderColor: theme.palette.divider,
			backgroundColor: 'transparent',
		},
	},
	slotLabel: {
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: 0.5,
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
	},
	slotValue: {
		fontSize: 14,
		fontWeight: 600,
	},
	slotEmpty: {
		fontSize: 14,
		fontStyle: 'italic',
		color: theme.palette.text.secondary,
	},
	slotMeta: {
		fontSize: 12,
		color: theme.palette.text.secondary,
	},
	driverGrid: {
		maxHeight: 380,
		overflow: 'auto',
		marginTop: theme.spacing(1),
	},
	driverCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 4,
		cursor: 'pointer',
		transition: 'all 0.15s',
		'&:hover': {
			borderColor: theme.palette.primary.light,
			backgroundColor: '#f5f5f5',
		},
	},
	selectedDriverCard: {
		borderColor: theme.palette.primary.main,
		backgroundColor: '#e3f2fd',
		borderWidth: 2,
	},
	avatar: {
		width: 40,
		height: 40,
		backgroundColor: theme.palette.primary.main,
	},
	searchField: {
		marginBottom: theme.spacing(2),
	},
	slotTag: {
		height: 18,
		fontSize: 10,
		fontWeight: 700,
	},
}));

const PRIMARY = 'primary';
const SECONDARY = 'secondary';

const driverFullName = (driver) =>
	driver ? `${driver.first_name || ''} ${driver.last_name || ''}`.trim() : '';

const DriverAssignDialog = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const alert = useAlert();
	const {driverDialogOpen, selectedOrder, drivers} = state;

	// Both slots are held at once so a single Assign can save the pair.
	const [primaryDriverId, setPrimaryDriverId] = useState(null);
	const [secondaryDriverId, setSecondaryDriverId] = useState(null);
	const [activeSlot, setActiveSlot] = useState(PRIMARY);
	const [loading, setLoading] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	useEffect(() => {
		if (driverDialogOpen && selectedOrder) {
			setPrimaryDriverId(selectedOrder.driver_id || null);
			setSecondaryDriverId(selectedOrder.secondary_driver || null);
			setActiveSlot(PRIMARY);
			setSearchQuery('');
		}
	}, [driverDialogOpen, selectedOrder]);

	const handleClose = () => {
		setPrimaryDriverId(null);
		setSecondaryDriverId(null);
		setActiveSlot(PRIMARY);
		setSearchQuery('');
		actions.closeDriverDialog();
	};

	// Filter drivers based on search query
	const filteredDrivers = drivers.filter((driver) => {
		if (!searchQuery) return true;
		const fullName = driverFullName(driver).toLowerCase();
		const phone = (driver.phone || '').toLowerCase();
		const query = searchQuery.toLowerCase();
		return fullName.includes(query) || phone.includes(query);
	});

	// A driver can hold only one of the two slots, so picking someone who already
	// fills the other slot moves them rather than duplicating them.
	const handlePickDriver = (driverId) => {
		if (activeSlot === SECONDARY) {
			if (!primaryDriverId) return;
			setSecondaryDriverId(driverId);
			if (driverId === primaryDriverId) setPrimaryDriverId(null);
			return;
		}
		setPrimaryDriverId(driverId);
		if (driverId === secondaryDriverId) setSecondaryDriverId(null);
	};

	const handleClearSlot = (event, slot) => {
		event.stopPropagation();
		if (slot === SECONDARY) {
			setSecondaryDriverId(null);
			return;
		}
		// Without a primary there is no secondary, so clearing the primary clears both.
		setPrimaryDriverId(null);
		setSecondaryDriverId(null);
		setActiveSlot(PRIMARY);
	};

	if (!selectedOrder) return null;

	const findDriver = (driverId) => (driverId ? drivers.find((d) => d.id === driverId) || null : null);

	const savedPrimaryId = selectedOrder.driver_id || null;
	const savedSecondaryId = selectedOrder.secondary_driver || null;
	const pickedPrimary = findDriver(primaryDriverId);
	const pickedSecondary = findDriver(secondaryDriverId);

	// What Assign would actually send. Each slot is its own endpoint, so an
	// untouched slot contributes no request.
	const buildTasks = () => {
		const tasks = [];
		const orderId = selectedOrder.order_id;

		if (primaryDriverId !== savedPrimaryId) {
			tasks.push({
				label: 'Primary driver',
				removal: !primaryDriverId,
				run: () => API.updateOrder({orderId}, {driver_id: primaryDriverId}).then((res) => res.json()),
			});
		}

		if (secondaryDriverId !== savedSecondaryId) {
			tasks.push(
				secondaryDriverId
					? {
						label: 'Secondary driver',
						removal: false,
						run: () => API.addSecondaryDriver({orderId}, {driver_id: secondaryDriverId}).then((res) => res.json()),
					}
					: {
						label: 'Secondary driver',
						removal: true,
						run: () => API.removeSecondaryDriver({orderId}, {driver_id: savedSecondaryId}).then((res) => res.json()),
					}
			);
		}

		return tasks;
	};

	const pendingTasks = buildTasks();

	const handleAssign = async () => {
		const tasks = buildTasks();

		if (tasks.length === 0) {
			alert.warning('Nothing to save', 'Pick a driver, or change one of the two slots first.');
			return;
		}

		const removals = tasks.filter((task) => task.removal);
		if (removals.length > 0) {
			const result = await alert.confirm(
				'Unassign driver',
				`This will take ${removals.map((task) => task.label.toLowerCase()).join(' and ')} off order #${selectedOrder.order_id}.`,
				'Yes, save changes',
				'Cancel'
			);
			if (!result.isConfirmed) return;
		}

		setLoading(true);
		try {
			// Both requests go out together rather than one after the other. Each
			// task settles into a result of its own, so one failing never stops the
			// other from completing or hides which half of the pair went wrong.
			const results = await Promise.all(
				tasks.map((task) =>
					task.run().then(
						(response) => ({
							label: task.label,
							ok: !!(response && response.success),
							message: response && response.message,
						}),
						(error) => ({label: task.label, ok: false, message: error.message})
					)
				)
			);

			const failed = results.filter((result) => !result.ok);
			const detail = failed
				.map((result) => `${result.label}: ${result.message || 'request failed'}`)
				.join('\n');

			if (failed.length === 0) {
				alert.success(
					'Success',
					tasks.length > 1
						? 'Both drivers assigned successfully!'
						: `${tasks[0].label} updated successfully!`
				);
				handleClose();
				setTimeout(() => {
					actions.refreshData();
				}, 1000);
				return;
			}

			if (failed.length === results.length) {
				// Nothing landed, so leave the dialog open with the picks intact to retry.
				alert.error('Error', detail);
				return;
			}

			// One slot saved and the other did not. Name the failure and refresh, so a
			// retry starts from what the server actually holds now.
			alert.warning('Partly saved', detail);
			handleClose();
			setTimeout(() => {
				actions.refreshData();
			}, 1000);
		} finally {
			setLoading(false);
		}
	};

	const renderSlot = (slot) => {
		const isPrimarySlot = slot === PRIMARY;
		const driver = isPrimarySlot ? pickedPrimary : pickedSecondary;
		const driverId = isPrimarySlot ? primaryDriverId : secondaryDriverId;
		const disabled = !isPrimarySlot && !primaryDriverId;
		const active = activeSlot === slot && !disabled;

		return (
			<Box
				onClick={() => !disabled && setActiveSlot(slot)}
				className={`${classes.slotCard} ${active ? classes.activeSlotCard : ''} ${
					disabled ? classes.disabledSlotCard : ''
				}`}
			>
				<Box display="flex" alignItems="center" justifyContent="space-between">
					<Typography className={classes.slotLabel}>
						{isPrimarySlot ? 'Primary driver' : 'Secondary driver'}
					</Typography>
					{driverId && (
						<Tooltip title={isPrimarySlot ? 'Clear both slots' : 'Clear secondary'}>
							<IconButton size="small" onClick={(e) => handleClearSlot(e, slot)}>
								<Close style={{fontSize: 16}}/>
							</IconButton>
						</Tooltip>
					)}
				</Box>
				{driver ? (
					<>
						<Typography className={classes.slotValue} noWrap>
							{driverFullName(driver) || `Driver #${driverId}`}
						</Typography>
						<Typography className={classes.slotMeta} noWrap>
							{driver.phone || 'No phone'}
						</Typography>
					</>
				) : (
					<Typography className={classes.slotEmpty}>
						{disabled ? 'Pick a primary first' : 'Not selected'}
					</Typography>
				)}
			</Box>
		);
	};

	return (
		<Dialog
			open={driverDialogOpen}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			className={classes.dialog}
		>
			<DialogTitle className={classes.dialogTitle}>
				<Box display="flex" alignItems="center" style={{gap: 8}}>
					<LocalShipping color="primary"/>
					<Typography variant="h6">Assign Drivers</Typography>
				</Box>
			</DialogTitle>

			<DialogContent style={{paddingTop: 24}}>
				{/* Order Info */}
				<Box className={classes.orderInfo}>
					<Typography variant="subtitle2" color="textSecondary">
						Order Details
					</Typography>
					<Typography variant="h6">
						Order #{selectedOrder.order_id}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Customer: {selectedOrder.customer_detail?.name || 'N/A'}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						Amount: ${parseFloat(selectedOrder.order_total || 0).toFixed(2)}
					</Typography>
					{/* Currently saved assignment, so pending picks can be read against it */}
					<Box mt={1} display="flex" flexWrap="wrap" style={{gap: 8}}>
						{savedPrimaryId ? (
							<Chip
								label={`Saved primary: ${driverFullName(findDriver(savedPrimaryId)) || `#${savedPrimaryId}`}`}
								size="small"
								color="primary"
							/>
						) : (
							<Chip label="No driver assigned yet" size="small" variant="outlined"/>
						)}
						{savedSecondaryId && (
							<Chip
								label={`Saved secondary: ${driverFullName(findDriver(savedSecondaryId)) || `#${savedSecondaryId}`}`}
								size="small"
								color="secondary"
							/>
						)}
					</Box>
				</Box>

				{/* Slot picker - the grid below fills whichever slot is active */}
				<Grid container spacing={2} style={{marginBottom: 16}}>
					<Grid item xs={12} sm={6}>{renderSlot(PRIMARY)}</Grid>
					<Grid item xs={12} sm={6}>{renderSlot(SECONDARY)}</Grid>
				</Grid>

				{/* Search Field */}
				<TextField
					fullWidth
					variant="outlined"
					size="small"
					placeholder="Search by name or phone..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					className={classes.searchField}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search/>
							</InputAdornment>
						),
					}}
				/>

				{/* Driver Grid */}
				<Typography variant="subtitle2" gutterBottom>
					Select {activeSlot === SECONDARY ? 'secondary' : 'primary'} driver ({filteredDrivers.length} available)
				</Typography>
				<Box className={classes.driverGrid}>
					{filteredDrivers.length === 0 ? (
						<Typography variant="body2" color="textSecondary" align="center" style={{padding: 32}}>
							{searchQuery ? 'No drivers found matching your search' : 'No drivers available'}
						</Typography>
					) : (
						<Grid container spacing={2}>
							{filteredDrivers.map((driver) => {
								const isPrimaryPick = driver.id === primaryDriverId;
								const isSecondaryPick = driver.id === secondaryDriverId;
								const isActivePick = activeSlot === SECONDARY ? isSecondaryPick : isPrimaryPick;

								return (
									<Grid item xs={12} sm={6} md={4} key={driver.id}>
										<Box
											onClick={() => handlePickDriver(driver.id)}
											className={`${classes.driverCard} ${
												isActivePick ? classes.selectedDriverCard : ''
											}`}
											p={1.5}
										>
											<Box display="flex" alignItems="center" style={{gap: 12}}>
												<Avatar className={classes.avatar}>
													<SportsMotorsportsIcon/>
												</Avatar>
												<Box flex={1} minWidth={0}>
													<Typography variant="body2" style={{fontWeight: 600}} noWrap>
														{driverFullName(driver) || `Driver #${driver.id}`}
													</Typography>
													<Typography variant="caption" color="textSecondary" noWrap>
														{driver.phone || 'No phone'}
													</Typography>
													{/* Marks a driver already holding a slot, including the
													    one the grid is not currently filling */}
													{(isPrimaryPick || isSecondaryPick) && (
														<Box mt={0.5}>
															<Chip
																className={classes.slotTag}
																label={isPrimaryPick ? 'PRIMARY' : '2ND'}
																size="small"
																color={isPrimaryPick ? 'primary' : 'secondary'}
															/>
														</Box>
													)}
												</Box>
												{isActivePick && (
													<Radio checked readOnly color="primary" size="small"/>
												)}
											</Box>
										</Box>
									</Grid>
								);
							})}
						</Grid>
					)}
				</Box>
			</DialogContent>

			<DialogActions>
				<Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
					<Typography variant="caption" color="textSecondary">
						{pendingTasks.length === 0
							? 'No changes yet'
							: `${pendingTasks.length} request${pendingTasks.length > 1 ? 's' : ''} will be sent`}
					</Typography>
					<Box display="flex" style={{gap: 8}}>
						<Button onClick={handleClose} disabled={loading}>
							Cancel
						</Button>
						<Button
							onClick={handleAssign}
							color="primary"
							variant="contained"
							disabled={loading || pendingTasks.length === 0}
							startIcon={loading && <CircularProgress size={20}/>}
						>
							{loading ? 'Saving...' : (pendingTasks.length > 1 ? 'Assign both' : 'Assign')}
						</Button>
					</Box>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default DriverAssignDialog;
